import { useState } from "react";
import HardLink from "@/components/HardLink";
import logoFull from "@/assets/logo-wordmark.png";
import { Menu, X, ShoppingCart, Search, Heart, User, LogOut, Package, ChevronDown, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCart } from "@/contexts/CartContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import SearchModal from "@/components/search/SearchModal";
import AuthModal from "@/components/auth/AuthModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const { totalItems, setIsCartOpen } = useCart();
  const { totalItems: wishlistItems } = useWishlist();
  const { user, profile, signOut, isLoading } = useAuth();
  const { isAdmin } = useIsAdmin();

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "Shop", href: "/shop" },
    { name: "Over Ons", href: "/about" },
    { name: "Contact", href: "/contact" },
    { name: "FAQ", href: "/faq" },
  ];

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="container mx-auto px-4">
          <div className="flex items-center h-20">
            {/* Logo */}
            <div className="flex-shrink-0">
              <HardLink 
                to="/" 
                className="flex items-center gap-2"
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              >
                <img 
                  src={logoFull} 
                  alt="ALIENJUICE - Premium Auto & Motor Detailing" 
                  className="h-7 sm:h-10 w-auto max-w-[120px] sm:max-w-[200px] object-contain"
                />
              </HardLink>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex md:flex-row flex-1 justify-center items-center gap-8">
              {navLinks.map((link) => (
                <HardLink
                  key={link.name}
                  to={link.href}
                  className="text-base font-medium text-muted-foreground hover:text-primary transition-colors link-hover whitespace-nowrap"
                >
                  {link.name}
                </HardLink>
              ))}
            </nav>

            {/* Actions */}
            <div className="flex-1 flex justify-end items-center gap-1 sm:gap-2 md:gap-4">
              <button 
                onClick={() => setIsSearchOpen(true)}
                className="p-2 text-muted-foreground hover:text-primary transition-colors"
                aria-label="Zoeken"
              >
                <Search className="w-5 h-5" />
              </button>
              <HardLink 
                to="/wishlist"
                className="p-2 text-muted-foreground hover:text-primary transition-colors relative"
                aria-label="Favorieten"
              >
                <Heart className="w-5 h-5" />
                {wishlistItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-bold">
                    {wishlistItems}
                  </span>
                )}
              </HardLink>
              <button 
                onClick={() => setIsCartOpen(true)}
                className="p-2 text-muted-foreground hover:text-primary transition-colors relative"
                aria-label="Winkelwagen"
              >
                <ShoppingCart className="w-5 h-5" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center font-bold">
                    {totalItems}
                  </span>
                )}
              </button>

              {/* Language Switcher */}
              <LanguageSwitcher />

              {/* User Menu */}
              {!isLoading && (
                user ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1 p-2 text-muted-foreground hover:text-primary transition-colors">
                        <User className="w-5 h-5" />
                        <ChevronDown className="w-3 h-3" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                      <div className="px-3 py-2">
                        <p className="text-sm font-medium text-foreground">
                          {profile?.first_name || "Welkom"}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {user.email}
                        </p>
                      </div>
                      <DropdownMenuSeparator />
                      {isAdmin && (
                        <DropdownMenuItem asChild>
                          <HardLink to="/admin" className="flex items-center gap-2 cursor-pointer text-primary">
                            <Shield className="w-4 h-4" />
                            Admin Dashboard
                          </HardLink>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <HardLink to="/account" className="flex items-center gap-2 cursor-pointer">
                          <User className="w-4 h-4" />
                          Mijn Account
                        </HardLink>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <HardLink to="/account/orders" className="flex items-center gap-2 cursor-pointer">
                          <Package className="w-4 h-4" />
                          Bestellingen
                        </HardLink>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={signOut}
                        className="flex items-center gap-2 cursor-pointer text-destructive focus:text-destructive"
                      >
                        <LogOut className="w-4 h-4" />
                        Uitloggen
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setIsAuthOpen(true)}
                  >
                    <User className="w-4 h-4 sm:hidden" />
                    <span className="hidden sm:inline">Inloggen</span>
                  </Button>
                )
              )}

              {/* Mobile Menu Toggle */}
              <button
                className="md:hidden p-2 text-muted-foreground hover:text-primary transition-colors"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
              >
                {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden py-4 border-t border-border animate-fade-in">
              <div className="flex flex-col gap-4">
                {navLinks.map((link) => (
                  <HardLink
                    key={link.name}
                    to={link.href}
                    className="text-lg font-medium text-muted-foreground hover:text-primary transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {link.name}
                  </HardLink>
                ))}
                {user && (
                  <>
                    <HardLink
                      to="/account"
                      className="text-lg font-medium text-muted-foreground hover:text-primary transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      Mijn Account
                    </HardLink>
                    <button
                      onClick={() => {
                        signOut();
                        setIsMenuOpen(false);
                      }}
                      className="text-lg font-medium text-destructive text-left"
                    >
                      Uitloggen
                    </button>
                  </>
                )}
              </div>
            </nav>
          )}
        </div>
      </header>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} />
    </>
  );
};

export default Header;
